import React, { useMemo } from 'react';
import {
  ButtonApprove,
  ButtonReject,
  FieldDatepicker,
  FieldInput,
  FormCol,
  FormCol12,
  FormCol3,
  EmptyFormRow,
  FormRow,
  promiseModalDialog,
  toastSuccess,
  useIsFieldRequiredHook,
} from 'motion-components';
import { useFormikContext } from 'formik';
import { useRootStore } from 'store';
import { useHandleError } from 'shared-pages/services';
import { useTranslation } from 'react-i18next';
import {
  FieldSelectNomos,
  FieldSelectProfession,
  FieldSelectTaxoffice,
} from 'shared-pages/components';
import {
  ACCEPT_COLLECTION_POINT_REQUEST,
  findSimilarCollectionPoints,
  REJECT_COLLECTION_POINT_REQUEST,
} from '../graphql';
import { BadgeApproval } from '../BadgeApproval';
import { useApolloClient, useMutation } from '@apollo/client';
import { promiseModalInput } from 'motion-components';
import promiseModalSelectCp from '../JudgeFlowModal/JudgeModal';
import { CollectionpointRequestLinesTable } from './collectionpointRequestLinesTable';
import { generatePath, Link } from 'react-router-dom';
import { getRouteByName } from 'app/routes';
import { isAfisCyprus, isFotokiklosi, isMedicycle } from 'shared-pages/common/constants';
import { useTriggerMailService } from 'shared-pages/hooks/trigger-mail-service';
import { Col, Row } from 'react-bootstrap';

export const FormFields = ({
  isNew,
  updateRecord,
  refetch,
  data,
  validationSchema = undefined,
}) => {
  const { appStore, authStore } = useRootStore();
  const { triggerMailService } = useTriggerMailService();
  const client = useApolloClient();
  const frmkCntx: any = useFormikContext();
  const { values } = frmkCntx;
  const isFieldRequired = useIsFieldRequiredHook(validationSchema);

  const { handleGraphqlError } = useHandleError('CollectionPointRequestFormFields');
  const { t, i18n } = useTranslation();
  const [accept] = useMutation(ACCEPT_COLLECTION_POINT_REQUEST);
  const [reject] = useMutation(REJECT_COLLECTION_POINT_REQUEST);

  const canEditData = useMemo(() => {
    const canEdit = isNew || data?.isApproved === null;
    const canEditReasoning = '';
    return { canEdit, canEditReasoning };
  }, [isNew, data?.isApproved]);

  const { canEdit } = canEditData;

  const handleAccept = async () => {
    const confirmed = await promiseModalDialog({
      title: t('Έγκριση Νέου Σημείου Παραλαβής'),
      text: t('Να Εγκριθεί η Αίτηση το Σημείου Παραλαβής;'),
    });
    if (!confirmed) return;

    try {
      // 1️⃣ Find duplicates
      const ret = await findSimilarCollectionPoints({
        client,
        name: values.name,
        companyVat: values.companyVat,
        postalCode: values.postalCode,
      });

      let collectionPointId = null;

      // 2️⃣ If duplicates exist, allow the user to choose
      if (ret?.recordCount > 0) {
        collectionPointId = await promiseModalSelectCp({
          title: 'Βρέθηκαν παρόμοια σημεία συλλογής',
          text: 'Επιλέξτε ένα από την λίστα ή ακυρώστε για να δημιουργηθεί νέο.',
          data: ret.data,
          newData: values,
        });

        if (collectionPointId === undefined || collectionPointId === null) {
          return; // User canceled the modal
        }
      }

      const variables = {
        id: data.id, // CollectionPointRequest ID
        collectionpointId: collectionPointId || null, // Pass the selected ID or null
      };

      console.log('🚀 Mutation Variables:', variables); // ✅ Debug log before mutation

      // 3️⃣ Call mutation with correct ID
      const res = await accept({ variables });

      console.log('✅ Mutation Response:', res); // ✅ Debug log response

      if (!res?.data?.acceptCollectionpointRequest) {
        throw new Error('❌ Mutation response is missing expected data');
      }

      toastSuccess(`Επιτυχής Δημιουργία Σημείου [${res.data.acceptCollectionpointRequest.id}]`);
      refetch();
    } catch (error) {
      console.error('❌ Mutation Error:', error);
      handleGraphqlError('accept', error);
    }
  };

  const handleReject = async () => {
    try {
      // Prompt user for reason
      const reason = await promiseModalInput({
        title: 'Αιτιολόγηση Απόρριψης',
        text: 'Αιτιολογήστε τον λόγο της απόρριψης',
      });

      // if user canceled or closed the dialog
      if (!reason) return;

      const res = await reject({
        variables: {
          id: data.id, // or idNum
          reason,
          judgedByUser: appStore?.userFullName ?? '',
          judgeDate: new Date().toISOString(),
        },
      });

      if (res.data.rejectCollectionPointRequest !== null) {
        triggerMailService();
      }

      toastSuccess(
        `Η αίτηση απορρίφθηκε με επιτυχία (ID: ${res.data.rejectCollectionPointRequest.id})`,
      );
      refetch();
    } catch (error) {
      handleGraphqlError('reject', error);
    }
  };

  return (
    <div className="d-flex flex-column">
      <div className="d-flex flex-row">
        {/* 1st Column */}
        <FormCol>
          <FieldInput
            required
            label={t('Επωνυμία')}
            name="name"
            placeholder=""
            disabled={!canEdit}
          />{' '}
          <FieldInput
            required
            label={t('Διεύθυνση')}
            name="addressLine1"
            placeholder=""
            disabled={!canEdit}
          />
          <FieldSelectProfession
            name={'professionId'}
            defaultValues={{ data }}
            disabled={!canEdit}
          />
          <FieldInput label={t('Περιοχή')} name="addressArea" placeholder="" disabled={!canEdit} />
          <FieldInput label={t('Πόλη')} name="addressCity" placeholder="" disabled={!canEdit} />
          <FieldInput label={t('T.K.')} name="postalCode" placeholder="" disabled={!canEdit} />
          {!(isAfisCyprus || isMedicycle) && (
            <Row>
              <FieldSelectNomos
                as={Col}
                name={'addressNomos'}
                defaultValues={{ data }}
                required={isFieldRequired('addressNomos', values)}
                disabled={!canEdit}
              />
            </Row>
          )}
        </FormCol>

        {/* 2nd Column */}
        <FormCol>
          <FieldInput
            label={t('Υπεύθυνος')}
            name="contactPerson"
            placeholder=""
            disabled={!canEdit}
          />
          <FieldInput
            required
            label={t('Τηλέφωνο 1')}
            name="phoneNumber1"
            placeholder=""
            disabled={!canEdit}
          />
          <FieldInput label={t('Παρατηρήσεις')} name="notes" placeholder="" disabled={!canEdit} />
          <FieldInput label={t('E-mail')} name="email" placeholder="" disabled={!canEdit} />
          <FieldInput
            label={t('Α.Φ.Μ.')}
            name="companyVat"
            placeholder=""
            disabled={!canEdit}
            required
          />
          {!isAfisCyprus &&
            !isMedicycle &&
            (isFotokiklosi ? (
              <FieldSelectTaxoffice
                // as={FormCol12}
                disabled={!canEdit}
                required={isFieldRequired('companyTaxOffice', values)}
              />
            ) : (
              <FieldInput
                label={t('Δ.Ο.Υ.')}
                name="companyTaxOffice"
                placeholder=""
                disabled={!canEdit}
                required={isFieldRequired('companyTaxOffice', values)}
              />
            ))}
        </FormCol>

        {/* 3rd Column */}
        <FormCol>
          {!isNew && (
            <>
              <div className="d-flex   align-items-start  flex-column mb-4">
                <span>{t('Κατάσταση Αξιολόγησης')} :</span>
                <BadgeApproval approvalStatus={data?.isApproved} className="pt-2" />
              </div>
              <FieldInput
                label={t('Αιτιολόγηση Αξιολόγησης')}
                name="judgeNotes"
                placeholder=""
                disabled={true}
              />
              <FieldInput
                label={t('Αξιολογήθηκε από')}
                name="judgedByUser"
                placeholder=""
                disabled={true}
              />
              <FieldDatepicker
                label={t('Ημερομηνία Αξιολόγησης')}
                name="judgeDate"
                disabled={true}
              />
              {data?.isApproved && data?.collectionpointId && (
                <div className=" d-flex flex-column">
                  <Link
                    to={generatePath(getRouteByName('Collectionpoint'), {
                      id: data.collectionpointId ?? 0,
                    })}
                  >
                    {t('Σχετικό Σημείο Συλλογής')}:
                  </Link>{' '}
                  <FieldInput name="collectionpoint.name" placeholder="" disabled={true} />
                </div>
              )}
            </>
          )}
          <EmptyFormRow />
          <FormRow>
            <FormCol></FormCol>
            <FormCol></FormCol>
            {data?.isApproved === null ? (
              <div className="w-100">
                <ButtonApprove
                  onClick={() => handleAccept()}
                  size="sm"
                  text={t('Αποδοχή')}
                  disabled={data.requestLines?.length === 0}
                  className="col-6"
                  style={{ maxWidth: '49%' }}
                />

                <ButtonReject
                  onClick={() => handleReject()}
                  size="sm"
                  text={t('Απόρριψη')}
                  className="col-6 float-right"
                  style={{ maxWidth: '49%' }}
                />
              </div>
            ) : null}
          </FormRow>
        </FormCol>
        <FormCol>
          <EmptyFormRow />
        </FormCol>
      </div>
      <div className="mb-5">
        {values && values?.requestLines && (
          <CollectionpointRequestLinesTable
            isNew={isNew}
            lines={values.requestLines}
            canEdit={canEdit}
          />
        )}
      </div>
    </div>
  );
};
